import { Button, H5, H6 } from "@sellar-io/components";
import { Transition } from "@headlessui/react";
import { OrderListResponseType } from "@sellar-io/api-v2/types";
import { formatCurrency } from "@sellar-io/common";
import dayjs from "dayjs";
import { Fragment, ReactElement, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { twMerge } from "tailwind-merge";
import { Icon, ListElement, ProfilePicture } from "../../../../components";
import { BadgeOrange, BadgeBlack } from "../../../../components/badge/Badge";
import { useDashboardContext } from "../../../../components/dashboard/Dashboard.shared";
import { SellarCreditAddToTabButton } from "../../../../components/newOrderSummary/Buttons/SellarCreditAddToTabButton";
import { OpeningOrderFeeLabel } from "../../../../components/openingOrderFee/OpeningOrderFeeLabel";
import { Toast } from "../../../../components/toast/Toast";
import { useToast } from "../../../../components/toast/useToast.shared";
import { ORDER_DETAILS } from "../../../../constants/routes";
import { useOrderAccept } from "../../../../hooks/api/v2/order/v2/useOrderAccept";
import { useQueryCache } from "../../../../hooks/api/v2/queryCache";
import { useSupplierRelationshipList } from "../../../../hooks/api/v2/supplierRelationship/useSupplierRelationshipList";
import { useFirebaseUser } from "../../../../hooks/useFirebaseUser.shared";
import { useSellarCurrentBusiness } from "../../../../hooks/useSellarCurrentBusiness.shared";
import { useShowTermsAndConditionsModal } from "../../../../hooks/useShowTermsAndConditionsModal";
import { TermsAndConditionsDialog } from "../../../settings/termsAndConditions/TermsAndConditionsDialog";
import { getOrderAcceptRedirectUrl } from "../../helpers/AcceptOrdersHelper/getOrderAcceptRedirectUrl";
import OrderFulfillmentIndicator from "../../orderFulfillmentIndicator/OrderFulfillmentIndicator";
import PaymentStatusIndicator from "../../paymentStatusIndicator/PaymentStatusIndicator";
import { usePaymentTermsIndicatorDirect } from "../../../../components/paymentTypeIndicator/usePaymentTermsIndicatorDirect";
import { TooltipContainer } from "@sellar-io/components";
import { useNavigateToChild } from "../../../../hooks/useParentNavigation";
import { SellarDistributionSupplierCutOffAlertContainer } from "../../Alerts/SellarDistributionSupplierCutOffAlert";

export default function RequestedOrderListElement({
  order,
  selectedOrderId,
}: {
  order: OrderListResponseType[number];
  selectedOrderId: number | null;
}): ReactElement {
  const { business } = useSellarCurrentBusiness();
  const location = useLocation();
  const navigateToChild = useNavigateToChild();
  const [isShowing, setIsShowing] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const navigateToOrderDetails = () => {
    if (selectedOrderId === order.id || isLoading) {
      return;
    }
    const orderUrl = `${ORDER_DETAILS.href({ orderId: order.id })}${location.search}`;
    navigateToChild(orderUrl);
  };
  const showSellarCreditFeatures = order.paymentType === "sellarCredit";

  const supplierRelationshipQuery = useSupplierRelationshipList(
    {
      query: [
        {
          type: "buyerId",
          modifier: "EQUALS",
          value: order.Retailer.id,
        },
      ],
      limit: 1,
    },
    {
      enabled: !!order.Retailer.id,
    },
  );

  const connection = supplierRelationshipQuery.data?.pages[0]?.[0];

  const openingOrderFee = order.OpeningOrderFee;

  const isSupplier = !!business?.Supplier;
  const businessDetails = isSupplier
    ? order.Retailer.Business
    : order.Supplier.Business;
  const businessName = businessDetails.name;
  const createdAt = order.createdAt;
  const createdTimeFromNow = dayjs().diff(dayjs(createdAt), "day");
  const orderIsDayOld = createdTimeFromNow >= 1;
  const daysAgoRequested = dayjs(order.createdAt).fromNow();
  const isSelectedOrder = selectedOrderId === order.id;
  const estimatedDelivery = order.deliveryPlannedAt;
  const estimatedDeliveryFormatted = dayjs(estimatedDelivery).format("ddd D");
  const deliveryTimeFromNow = dayjs(estimatedDelivery).diff(dayjs(), "hours");
  const deliveryIsLessOrEqual48Hours = deliveryTimeFromNow <= 48;
  const pendingRequestedTerms =
    connection?.PaymentTermsRequest?.status === "pending";

  const { full: sellarCreditIcon } = usePaymentTermsIndicatorDirect({
    indicatorType: "sellarCredit",
    tooltipContent: "Sellar Credit",
    overrideText: "",
  });

  const isSellarCredit = order.paymentType === "sellarCredit";

  const sellarCreditOrderHasBeenAddedToTab =
    order.TwoOrder?.verificationStatus === "verified" &&
    order.TwoOrder.state === "approved";

  const showAwaitingConfirmation =
    isSupplier &&
    isSellarCredit &&
    order.initiator === "supplier" &&
    !sellarCreditOrderHasBeenAddedToTab;

  const showAcceptButton =
    !pendingRequestedTerms &&
    !supplierRelationshipQuery.isPending &&
    isSupplier &&
    !showAwaitingConfirmation;

  const paymentTypeIsCardPaymentWithDeposit =
    order.paymentType === "cardPaymentWithDeposit";

  return (
    <Transition.Root show={isShowing} as={Fragment}>
      <Transition.Child
        as={Fragment}
        enter="transform transition ease-in-out duration-500"
        enterFrom="translate-x-full"
        enterTo="translate-x-0"
        leave="transform transition ease-in-out duration-500"
        leaveFrom="translate-x-0"
        leaveTo="translate-x-full"
      >
        <div>
          <ListElement
            promptText={""}
            selected={isSelectedOrder}
            className={""}
            icon={null}
          >
            <div className="flex w-full flex-row justify-between">
              <div
                className="flex w-10/12 w-full flex-row gap-4"
                onClick={navigateToOrderDetails}
              >
                <div className="flex items-center justify-center">
                  <ProfilePicture
                    name={businessName}
                    logoUrl={businessDetails.logoUrl}
                  />
                </div>
                <div className="flex w-full flex-col gap-2">
                  <div className="flex flex-col">
                    <div className="flex flex-row justify-between">
                      <H6 className={"text-sm"}>Order #{order.id}</H6>
                      {order.FailedCollectionResolution && !isSupplier && (
                        <BadgeOrange icon={"CAUTION"}>
                          Collection failed
                        </BadgeOrange>
                      )}
                      {connection?.PaymentTermsRequest?.status ===
                        "pending" && (
                        <BadgeOrange icon={"CAUTION"}>
                          Credit requested
                        </BadgeOrange>
                      )}
                    </div>
                    <div className="flex">
                      <H5>
                        {businessName} -{" "}
                        {formatCurrency(order.price, order.currency)}
                      </H5>
                      {!!openingOrderFee && isSupplier && (
                        <OpeningOrderFeeLabel fee={openingOrderFee} />
                      )}
                    </div>
                    <H6
                      className={twMerge(
                        "text-sm",
                        orderIsDayOld && "text-red-600",
                      )}
                    >
                      {daysAgoRequested}
                    </H6>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <OrderFulfillmentIndicator
                      fulfillmentType={order.fulfillmentType}
                    />
                    {showSellarCreditFeatures && sellarCreditIcon}
                    {paymentTypeIsCardPaymentWithDeposit && (
                      <TooltipContainer
                        enable={true}
                        text="Card payment with deposit"
                      >
                        <BadgeBlack icon={"DEPOSIT_PAYMENT"} />
                      </TooltipContainer>
                    )}
                    {!showSellarCreditFeatures && (
                      <PaymentStatusIndicator
                        {...order}
                        invoicesEnabled={order.Supplier.invoicesEnabled}
                      />
                    )}
                  </div>
                  <div className="mt-2 flex flex-col items-start gap-1 sm:flex-row sm:items-center sm:gap-4">
                    {estimatedDelivery && (
                      <p
                        className={twMerge(
                          deliveryIsLessOrEqual48Hours && "text-red-600",
                        )}
                      >
                        Estimated delivery: {estimatedDeliveryFormatted}
                      </p>
                    )}
                    <SellarDistributionSupplierCutOffAlertContainer
                      order={{
                        ...order,
                        supplierFinalisationCutOffAt:
                          order.OrderSellarDistributionMeta
                            ?.supplierFinalisationCutOffAt ?? null,
                      }}
                      mode="badge"
                    />
                  </div>
                </div>
              </div>
              <div className="flex flex-col items-center justify-center">
                <>
                  {isSupplier ? (
                    <>
                      {showAcceptButton ? (
                        <OrderAcceptedButton
                          orderId={order.id}
                          isSelectedOrder={isSelectedOrder}
                          setIsShowing={setIsShowing}
                          buyerName={order.Retailer.Business.name}
                          setIsLoading={setIsLoading}
                        />
                      ) : (
                        <>
                          <div className="inline-flex items-center gap-1 text-xs">
                            <Icon icon={"CLOCK_OUTLINE"} />
                            <H5 className={"text-xs"}>PENDING</H5>
                          </div>
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      {isSellarCredit && (
                        <SellarCreditAddToTabButton
                          buttonSize={"small"}
                          orderDeferredPaymentId={
                            order.Payment?.deferredPaymentId
                          }
                          initiator={order.initiator}
                          orderId={order.id}
                          isDisabled={
                            !business?.BusinessUser?.canAccessSellarCredit
                          }
                        />
                      )}
                    </>
                  )}
                </>
              </div>
            </div>
          </ListElement>
        </div>
      </Transition.Child>
    </Transition.Root>
  );
}

const OrderAcceptedButton = ({
  orderId,
  isSelectedOrder,
  setIsShowing,
  buyerName,
  setIsLoading,
}: {
  orderId: number;
  isSelectedOrder: boolean;
  setIsShowing: (isShowing: boolean) => void;
  setIsLoading: (isShowing: boolean) => void;
  buyerName: string;
}) => {
  const { user } = useFirebaseUser();
  const { business } = useSellarCurrentBusiness();
  const navigate = useNavigate();
  const { isTwoPanel } = useDashboardContext();
  const { toast } = useToast();
  const acceptOrderMutation = useOrderAccept(orderId);
  const queryCache = useQueryCache();

  const onSubmit = () => {
    setIsLoading(true);
    const redirectUrl = getOrderAcceptRedirectUrl({
      queryCache,
      orderId,
      uid: user?.uid,
      businessId: business?.id,
    });

    setIsShowing(false);

    return acceptOrderMutation.mutate(undefined, {
      onSuccess: () => {
        const toastMessage = `Moved to All orders. ${buyerName} has been informed that you’ve accepted their order.`;
        const isDesktopAndCurrentOrder = isSelectedOrder && isTwoPanel;
        toast({
          placement: "bottom",
          render: () => (
            <Toast icon={"CHECK_CIRCLE"} className={"max-w-full"}>
              {toastMessage}
            </Toast>
          ),
        });
        if (isDesktopAndCurrentOrder) {
          navigate(redirectUrl);
        }
      },
      onError: () => {
        setIsShowing(true);
        setIsLoading(false);
      },
    });
  };

  const [termsActive, setTermsActive] = useState(false);

  const termsAndConditionsEnabled = useShowTermsAndConditionsModal();

  const handleAcceptClick = () => {
    if (termsAndConditionsEnabled) {
      setTermsActive(true);
    } else {
      onSubmit();
    }
  };

  const handleTermsDismiss = (accepted: boolean) => {
    setTermsActive(false);
    if (accepted) onSubmit();
  };

  return (
    <>
      <Button
        size={"small"}
        onClick={handleAcceptClick}
        disabled={acceptOrderMutation.isPending}
      >
        <Icon size={0.75} icon={"THUMB_UP_OUTLINE"} />
        Accept
      </Button>
      <TermsAndConditionsDialog
        active={termsActive}
        onDismiss={handleTermsDismiss}
      />
    </>
  );
};
